import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Highlighter from 'react-highlight-words';
import css from './TagInputSuggestion.css';
import OverflowingText from '../foundations/OverflowingText';
import Icon, { IconVariants, IconSizes } from '../foundations/Icon';

const TagInputSuggestion = ({
    label = '',
    value = '',
    isDisabled = false,
    isSelected = false,
    selectOption = () => {},
    selectProps: { inputValue, onEditClick, onDeleteClick } = { inputValue: '' },
    innerProps = {},
    innerRef = () => {},
    data = {},
}) => {
    const handleEditClick = (optionData, e) => {
        e.stopPropagation();
        onEditClick(optionData);
    };

    const handleDeleteClick = (optionData, e) => {
        e.stopPropagation();
        onDeleteClick(optionData);
    };

    const { ...rest } = innerProps;
    const { extra, extraOnHover = '', subLabel, className = '', icon, iconSrc } = data;

    return (
        <div
            className={classNames(css.suggestionRow, className, {
                [css.disabled]: isDisabled,
                [css.selected]: isSelected,
            })}
            onClick={() => {
                if (isDisabled) return;
                selectOption({ value, label });
            }}
            role="button"
            tabIndex={0}
            ref={innerRef}
            {...rest}
        >
            {(icon || iconSrc) && (
                <div className={css.icon}>
                    {icon || (iconSrc && <img className={css.iconImage} src={iconSrc} alt="" />)}
                </div>
            )}
            {data.subTitle && <div className={css.subTitle}>{data.subTitle}</div>}
            <OverflowingText>
                <Highlighter
                    searchWords={[inputValue]}
                    textToHighlight={label}
                    highlightClassName={css.highlighted}
                    autoEscape
                />
            </OverflowingText>
            {subLabel && (
                <OverflowingText>
                    <Highlighter
                        searchWords={[inputValue]}
                        textToHighlight={subLabel}
                        className={classNames(css.subLabel)}
                        highlightClassName={css.highlighted}
                        autoEscape
                    />
                </OverflowingText>
            )}
            {extra || extraOnHover ? (
                <div className={extra ? css.extra : css.extraOnHover}>{extra || extraOnHover}</div>
            ) : null}
            {(data.erasable || data.editable) && (
                <div className={css.editIcons}>
                    {data.erasable && (
                        <div className={css.trashIcon} onClick={e => handleDeleteClick(data, e)}>
                            <Icon name="trash" variant={IconVariants.LIGHT} size={IconSizes.MEDIUM} />
                        </div>
                    )}
                    {data.editable && (
                        <div className={css.editIcon} onClick={e => handleEditClick(data, e)}>
                            <Icon name="pencil" variant={IconVariants.LIGHT} size={IconSizes.MEDIUM} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const MemoizedTagInputSuggestion = React.memo(TagInputSuggestion, (prevProps, nextProps) => {
    // Return true if props are equal (don't re-render), false if different (re-render)
    return (
        prevProps.isFocused === nextProps.isFocused &&
        prevProps.isSelected === nextProps.isSelected &&
        prevProps.isDisabled === nextProps.isDisabled &&
        prevProps.selectProps.inputValue === nextProps.selectProps.inputValue
    );
});

TagInputSuggestion.propTypes = {
    label: PropTypes.string,
    value: PropTypes.any,
    selectOption: PropTypes.func,
    isFocused: PropTypes.bool,
    selectProps: PropTypes.objectOf(PropTypes.any),
    isSelected: PropTypes.bool,
    innerProps: PropTypes.objectOf(PropTypes.any),
    innerRef: PropTypes.func,
    data: PropTypes.objectOf(PropTypes.any),
    isDisabled: PropTypes.bool,
};

export default MemoizedTagInputSuggestion;
